import { useState } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { useToast } from "../../context/ToastContext";
import { useReceivables, useRefreshAll } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatDate, localName, money } from "../../lib/format";
import type { PaymentMethod, Receivable } from "../../lib/types";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";
import { Modal } from "../ui/Modal";
import { PaymentPicker } from "../ui/PaymentPicker";

function SettleDialog({ item, onClose }: { item: Receivable; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const [amount, setAmount] = useState(money(item.remaining));
  const [method, setMethod] = useState<PaymentMethod>("cliq");
  const [busy, setBusy] = useState(false);
  const fils = jod(Number(amount));
  const valid = !Number.isNaN(fils) && fils > 0 && fils <= item.remaining;

  async function settle() {
    setBusy(true);
    try {
      await api.post(`/receivables/${item.id}/settle`, { payments: [{ method, amount: fils }] });
      toast(t("debts.settled"), "success");
      refresh();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={onClose} title={t("debts.settleTitle")} size="sm"
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-dark" disabled={!valid || busy} onClick={settle}>{t("debts.confirmSettle")}</button></>}>
      <div className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-900">{item.description}<br /><span className="num font-mono">{t("debts.remaining")}: {money(item.remaining)} {t("currency")}</span></div>
      <div>
        <label className="label" htmlFor="settle-amt">{t("debts.amount")}</label>
        <input id="settle-amt" className="field num font-mono" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        {!valid && <p className="mt-1 text-[11px] font-bold text-red-600">{t("debts.amountRange", { max: money(item.remaining) })}</p>}
      </div>
      <PaymentPicker value={method} onChange={setMethod} methods={["cliq", "card", "cash"]} />
    </Modal>
  );
}

const TONE = { open: "gray", partial: "amber", overdue: "red", invoice_sent: "purple", settled: "green" } as const;

export function DebtLedger() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { data, isLoading } = useReceivables();
  const [settle, setSettle] = useState<Receivable | null>(null);
  const open = (data?.receivables ?? []).filter((r) => r.status !== "settled");

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("debts.title")}>
      <SectionHeader icon="🏢" title={t("debts.title")} subtitle={t("debts.subtitle")}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-extrabold text-solar-orange">{t("debts.totalPending")}: <span className="num">{money(data?.summary.pending ?? 0)}</span> {t("currency")}</span>
            <DownloadButton path={`/exports/receivables.xlsx?locale=${locale}`} fallbackName="receivables.xlsx">📊 Excel</DownloadButton>
            <DownloadButton path={`/exports/receivables.csv?locale=${locale}`} fallbackName="receivables.csv">CSV</DownloadButton>
            <DownloadButton path={`/documents/receivables-report?locale=${locale}`} fallbackName="receivables.pdf">📄 PDF</DownloadButton>
            <PrintButton path={`/documents/receivables-report?locale=${locale}`} />
          </div>
        } />
      {isLoading ? <Empty>{t("common.loading")}</Empty> : open.length === 0 ? <Empty>{t("debts.none")}</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-3 text-start">{t("debts.entity")}</th><th className="px-2 py-3 text-start">{t("debts.due")}</th>
                <th className="px-2 py-3 text-end">{t("debts.total")}</th><th className="px-2 py-3 text-end">{t("debts.paid")}</th><th className="px-2 py-3 text-end">{t("debts.remaining")}</th>
                <th className="px-2 py-3 text-center">{t("table.status")}</th><th className="px-3 py-3 text-end">{t("table.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {open.map((r) => (
                <tr key={r.id} className="hover:bg-purple-50/20">
                  <td className="px-3 py-3">
                    <div className="font-black text-cosmic-navy">{r.organizationName ? localName({ name: r.organizationName, nameAr: r.organizationNameAr }, locale) : r.guardianName}</div>
                    <div className="text-[10px] font-normal text-gray-400">{r.description}</div>
                  </td>
                  <td className="num px-2 py-3 font-mono text-[11px] text-gray-500">{r.dueDate ? formatDate(r.dueDate, locale) : "—"}</td>
                  <td className="num px-2 py-3 text-end font-mono">{money(r.total)}</td>
                  <td className="num px-2 py-3 text-end font-mono text-emerald-600">{money(r.paid)}</td>
                  <td className="px-2 py-3 text-end"><span className={`num rounded border px-2 py-0.5 font-mono font-black ${r.status === "overdue" ? "border-red-200 bg-red-50 text-red-600" : "border-amber-200 bg-amber-50 text-solar-orange"}`}>{money(r.remaining)} {t("currency")}</span></td>
                  <td className="px-2 py-3 text-center"><Pill tone={TONE[r.status]}>{t(`debts.status.${r.status}`)}</Pill></td>
                  <td className="px-3 py-3 text-end"><button type="button" className="btn-dark !px-2.5 !py-1 !text-[10px]" onClick={() => setSettle(r)}>{t("debts.settle")}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {settle && <SettleDialog item={settle} onClose={() => setSettle(null)} />}
    </section>
  );
}
